import React, { useState, useRef, useEffect } from 'react'
import { Calendar, ChevronLeft, ChevronRight, Clock } from 'lucide-react'
import { cn } from 'lib/utils'

interface ModernDatePickerProps {
  selected?: Date | null
  onChange: (date: Date | null) => void
  onBlur?: () => void
  placeholder?: string
  className?: string
  disabled?: boolean
  minDate?: Date
  maxDate?: Date
  showTimeSelect?: boolean
  error?: string
  label?: string
  required?: boolean
  description?: string
  smartPositioning?: boolean // Enable smart positioning (above/below based on available space)
}

export function ModernDatePicker({
  selected,
  onChange,
  onBlur,
  placeholder = "Sélectionner une date",
  className,
  disabled = false,
  minDate,
  maxDate,
  showTimeSelect = false,
  error,
  label,
  required = false,
  description,
  smartPositioning = false
}: ModernDatePickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [currentMonth, setCurrentMonth] = useState(selected || new Date())
  const [timeValue, setTimeValue] = useState(
    selected ? selected.toTimeString().slice(0, 5) : '09:00'
  )
  const [positionAbove, setPositionAbove] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        onBlur?.()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onBlur])

  useEffect(() => {
    if (smartPositioning && isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const spaceBelow = viewportHeight - buttonRect.bottom
      const spaceAbove = buttonRect.top
      const calendarHeight = 350 // Approximate height of the calendar
      
      // Position above if there's not enough space below but enough space above
      if (spaceBelow < calendarHeight && spaceAbove > spaceBelow) {
        setPositionAbove(true)
      } else {
        setPositionAbove(false)
      }
    } else {
      setPositionAbove(false)
    }
  }, [isOpen, smartPositioning])

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const formatTime = (date: Date) => {
    return date.toTimeString().slice(0, 5)
  }

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    const days = []
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day))
    }
    
    return days
  }

  const handleDateSelect = (date: Date) => {
    if (showTimeSelect && selected) {
      const [hours, minutes] = timeValue.split(':').map(Number)
      const newDate = new Date(date)
      newDate.setHours(hours, minutes)
      onChange(newDate)
    } else {
      onChange(date)
    }
    setIsOpen(false)
    onBlur?.()
  }

  const handleTimeChange = (time: string) => {
    setTimeValue(time)
    if (selected) {
      const [hours, minutes] = time.split(':').map(Number)
      const newDate = new Date(selected)
      newDate.setHours(hours, minutes)
      onChange(newDate)
    }
  }

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newMonth = new Date(currentMonth)
    if (direction === 'prev') {
      newMonth.setMonth(newMonth.getMonth() - 1)
    } else {
      newMonth.setMonth(newMonth.getMonth() + 1)
    }
    setCurrentMonth(newMonth)
  }

  const isDateDisabled = (date: Date) => {
    if (minDate && date < minDate) return true
    if (maxDate && date > maxDate) return true
    return false
  }

  const isSameDay = (date1: Date, date2: Date) => {
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear()
  }

  const days = getDaysInMonth(currentMonth)
  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ]
  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']

  return (
    <div className="space-y-2">
      {label && (
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
      <div className="relative" ref={dropdownRef}>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-destructive focus:ring-destructive",
            isOpen && "ring-2 ring-ring",
            className
          )}
        >
          <div className="flex items-center">
            <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
            <span className={selected ? "text-foreground" : "text-muted-foreground"}>
              {selected ? formatDate(selected) : placeholder}
            </span>
            {showTimeSelect && selected && (
              <span className="ml-2 text-muted-foreground">
                {formatTime(selected)}
              </span>
            )}
          </div>
          <ChevronLeft className="h-4 w-4 text-muted-foreground" />
        </button>

        {isOpen && (
          <div className={cn(
            "absolute z-50 w-80 rounded-md border border-input bg-background shadow-lg",
            smartPositioning && positionAbove ? "bottom-full mb-1" : "top-full mt-1"
          )}>
            {/* Calendar Header */}
            <div className="flex items-center justify-between p-3 border-b">
              <button
                type="button"
                onClick={() => navigateMonth('prev')}
                className="p-1 hover:bg-accent rounded-sm"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <h3 className="font-medium">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </h3>
              <button
                type="button"
                onClick={() => navigateMonth('next')}
                className="p-1 hover:bg-accent rounded-sm"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="p-3">
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map((day) => (
                  <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar days */}
              <div className="grid grid-cols-7 gap-1">
                {days.map((day, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => day && handleDateSelect(day)}
                    disabled={!day || isDateDisabled(day)}
                    className={cn(
                      "h-8 w-8 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed",
                      day && selected && isSameDay(day, selected) && "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                      day && !isDateDisabled(day) && "cursor-pointer"
                    )}
                  >
                    {day?.getDate()}
                  </button>
                ))}
              </div>
            </div>

            {/* Time Selector */}
            {showTimeSelect && (
              <div className="border-t p-3">
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <input
                    type="time"
                    value={timeValue}
                    onChange={(e) => handleTimeChange(e.target.value)}
                    className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  )
}
